import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Button, Drawer, Tooltip, Row, Col, Modal, Table, message } from "antd";
import { DeleteOutlined, EditFilled, ExclamationCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import styles from "./applications.module.css";
import { formatDate } from "../../utils/formatDate";
import { Loading } from "../../components/loading";
import { ApplicationForm } from "./applicationForm";
import $authHost from "../../http/auth";
import { CreateApplicationModal } from "./createApplicationModal";
import { getUser } from "../selectors";
import permissions from "../../constants/permissions";

const { confirm } = Modal;

interface Application {
  id: number;
  createdOn: string | null;
  updatedOn: string | null;
  deletedOn: string | null;
  name: string;
  description: string | null;
  isEnabled: boolean;
  currentVersion?: {
    id: number;
    createdOn: string;
    updatedOn: string;
    deletedOn: string;
    applicationId: number;
    schema: string;
    configuration: string;
    version: string;
    application: {
      id: number;
      createdOn: string;
      updatedOn: string;
      deletedOn: string;
      name: string;
      isEnabled: boolean;
      currentVersion: string;
    };
  };
}

export const Applications = () => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [selectedApplicationId, setSelectedApplicationId] = useState<string | null>(null);
  const [applicationModal, setApplicationModal] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState<boolean>(false);
  const user = useSelector(getUser);

  const userCanReadApplications = user.permissions.includes(permissions.MANAGE_APPLICATIONS_READ);
  const userCanWriteApplications = user.permissions.includes(permissions.MANAGE_APPLICATIONS_WRITE);
  const userCanDeleteApplications = user.permissions.includes(permissions.MANAGE_APPLICATIONS_DELETE);

  const { t } = useTranslation("applications");

  const sortById = (a: Application, b: Application) => Number(a.id) - Number(b.id);
  const sortByName = (a: Application, b: Application) => a.name.localeCompare(b.name);
  const sortByEnabled = (a: Application, b: Application) => Number(a.isEnabled) - Number(b.isEnabled);
  const sortByDescription = (a: Application, b: Application) => {
    if (!a.description && b.description) {
      return -1;
    }
    if (a.description && !b.description) {
      return 1;
    }
    if (!a.description && !b.description) {
      return 0;
    }
    if (a.description && b.description) {
      return a.description.localeCompare(b.description);
    }
    return 0;
  };
  const getSorterForDateProperty = (propertyName: "createdOn" | "updatedOn") => (a: Application, b: Application) => {
    if (a[propertyName] && !b[propertyName]) {
      return 1;
    }
    if (!a[propertyName] && b[propertyName]) {
      return -1;
    }
    if (!a[propertyName] && !b[propertyName]) {
      return 0;
    }
    return new Date(a[propertyName]!).getTime() - new Date(b[propertyName]!).getTime();
  };

  const columns = [
    {
      title: t("id"),
      dataIndex: "id",
      key: "id",
      width: "5%",
      sorter: sortById,
      render: (id: string) => {
        return (
          <span
            className={styles.idCell}
            onClick={() => {
              navigator.clipboard.writeText(id).then(() => message.success(t("idCopiedToClipboard")));
            }}
          >
            {id}
          </span>
        );
      },
    },
    {
      title: t("name"),
      dataIndex: "name",
      key: "name",
      width: "25%",
      sorter: sortByName,
      render: (value: string) => <span className={styles.noWrap}>{value}</span>,
    },
    {
      title: t("description"),
      dataIndex: "description",
      key: "description",
      width: "25%",
      sorter: sortByDescription,
      render: (value: string) => <span>{value}</span>,
    },
    {
      title: t("createdOn"),
      dataIndex: "createdOn",
      key: "createdOn",
      width: "20%",
      sorter: getSorterForDateProperty("createdOn"),
      render: (value: string) =>
        value ? <span className={styles.noWrap}>{formatDate(value)}</span> : <hr className={styles.emptyValue} />,
    },
    {
      title: t("updatedOn"),
      dataIndex: "updatedOn",
      key: "updatedOn",
      width: "20%",
      sorter: getSorterForDateProperty("updatedOn"),
      render: (value: string) =>
        value ? <span className={styles.noWrap}>{formatDate(value)}</span> : <hr className={styles.emptyValue} />,
    },
    {
      title: t("enabled"),
      dataIndex: "isEnabled",
      key: "isEnabled",
      width: "10%",
      sorter: sortByEnabled,
      render: (value: boolean) => value.toString(),
    },
    {
      title: t("actions"),
      dataIndex: "id",
      key: "actions",
      width: "15%",
      render: (id: string, record: any) => (
        <div className={styles.actionsContainer}>
          <Tooltip title={userCanWriteApplications ? t("editApplication") : t("permissionDenied")}>
            <Button
              className={styles.actionIcon}
              icon={<EditFilled />}
              onClick={() => showEditApplication(id)}
              disabled={!userCanWriteApplications}
              data-testid="editApplication"
            />
          </Tooltip>
          <Tooltip title={userCanDeleteApplications ? t("deleteApplication") : t("permissionDenied")}>
            <Button
              className={styles.actionIcon}
              icon={<DeleteOutlined />}
              onClick={() => {
                showConfirm(record);
              }}
              disabled={!userCanDeleteApplications}
              data-testid="deleteApplication"
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  useEffect(() => {
    getApplications();
  }, []);

  const getApplications = () => {
    $authHost
      .get("/Applications")
      .then((res) => {
        setApplications(res.data);
      })
      .catch((err) => {
        console.log(err?.message);
      })
      .finally(() => {
        setPageLoading(false);
      });
  };

  const showEditApplication = (id: string) => {
    setSelectedApplicationId(id);
    setEditDrawerOpen(true);
  };

  const hideEditApplication = () => {
    setSelectedApplicationId("");
    setEditDrawerOpen(false);
  };

  const onApplicationSave = (updatedApp: Application) => {
    hideEditApplication();
    setApplications(applications.map((app) => (app.id === updatedApp.id ? updatedApp : app)));
  };

  const deleteApplication = (id: number) => {
    setPageLoading(true);
    $authHost
      .delete(`/Applications/${id}`)
      .then(() => {
        setApplications((previousItems: Application[]) => {
          return previousItems.filter((item: Application) => item.id !== id);
        });
        message.success(t("applicationDeleted"));
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setPageLoading(false);
      });
  };

  const showConfirm = (application: Application) => {
    confirm({
      title: t("doYouWantToDeleteApplication", { name: application.name }),
      icon: <ExclamationCircleOutlined />,
      content: "",
      onOk() {
        deleteApplication(application.id);
      },
      onCancel() {
        console.log("Cancel", application);
      },
    });
  };

  const onApplicationModalClose = () => {
    setApplicationModal(false);
  };

  const onApplicationModalSubmit = (newApplication: Application) => {
    setApplications((prevApplications) => [...prevApplications, newApplication]);
  };

  return (
    <>
      <h1 className={styles.heading}>{t("applicationList")}</h1>
      <Row className={styles.searchRow}>
        <Col xs={24}>
          {pageLoading && <Loading />}
          <p>
            {userCanWriteApplications ? (
              <Button
                type="primary"
                onClick={() => {
                  setApplicationModal(true);
                }}
              >
                {t("createApplication")}
              </Button>
            ) : (
              <Tooltip title={t("permissionDenied")}>
                <Button type="primary" disabled>
                  {t("createApplication")}
                </Button>
              </Tooltip>
            )}
          </p>
        </Col>
      </Row>
      <Row>
        <Col xs={24}>
          {userCanReadApplications && (
            <Table columns={columns} scroll={{ x: true }} dataSource={applications} rowKey="id" />
          )}
        </Col>
      </Row>

      <CreateApplicationModal
        open={applicationModal}
        onClose={onApplicationModalClose}
        onSubmit={onApplicationModalSubmit}
      />

      <Drawer
        title={t("applicationSettings")}
        placement="right"
        width={window.innerWidth > 768 ? 700 : window.innerWidth}
        onClose={hideEditApplication}
        open={editDrawerOpen}
        mask={true}
        destroyOnClose={true}
      >
        <ApplicationForm applicationId={selectedApplicationId} onApplicationSave={onApplicationSave} />
      </Drawer>
    </>
  );
};
